// © Microsoft Corporation. All rights reserved.

import { IActivityLogItem } from "clientcore-infrastructure-analytics/models/IActivityLogItem";
import { ILoggingService } from "clientcore-infrastructure-analytics/ILoggingService";
import { LoggedExceptionManager } from "clientcore-infrastructure-analytics/LoggedExceptionManager";
import { canUseDOM } from "@msnews/isomorphic-util";

/**
 * ExponentialBackoffRetry implements retry logic for transient errors.
 * When encountering transient errors, the request is tried again and each retry is delayed exponentially using following calculation:
 * Math.min(this.baseRetryDelay * Math.pow(this.factor, retry), this.maxRetryDelay).
 * The request is retried until maximum retry count is reached or the request completes successfully or fails with a non-transient error.
 * Each delay is offset by a random number between a minimum and maximum offset range, so that in the cases of when there is a failure
 * where many clients fail at once (e.g. network outage) there is not a huge amount of requests at the same time to the server.
 * @class
 */
export class ExponentialBackoffRetry {

    /**
     * Default base delay between each retries.
     * @type {number}
     */
    private static defaultBaseRetryDelayInMs = 100;

    /**
     * Default exponential factor to use to increase each retry delay.
     * @type {number}
     */
    private static defaultFactor = 2;

    /**
     * Default maximum offset for each delay.
     * @type {number}
     */
    private static defaultMaxOffsetInMs = 25;

    /**
     * Default number of retries.
     * @type {number}
     */
    private static defaultMaxRetries = 5;

    /**
     * Default minimum offset for each delay.
     * @type {number}
     */
    private static defaultMinOffsetInMs = 1;

    /**
     * Maximum allowed number of retries.
     * @type {number}
     */
    private static maxAllowedRetries = 10;

    /**
     * Maximum allowed time in milliseconds to delay each retry.
     * @type {number}
     */
    private static maxAllowedRetryDelayInMs = 2000;

    /**
     * Base delay value in milliseconds to delay each retry.
     * Default value is 100.
     * @type {number}
     */
    private baseRetryDelayInMs: number;

    /**
     * The exponential factor to use for delaying each subsequent retry.
     * Default value is 2.
     * @type {number}
     */
    private factor: number;

    /**
     * The logging service
     * @type {ILoggingService}
     */
    private loggingService: ILoggingService;

    /**
     * Maximum delay offet in milliseconds.
     * Default value is 25.
     * @type {number}
     */
    private maxOffsetInMs: number;

    /**
     * Maximum number of retries.
     * Default is 5.
     * @type {number}
     */
    private maxRetries: number;

    /**
     * Maximum delay in milliseconds between each retry.
     * Default value is 2000.
     * @type {number}
     */
    private maxRetryDelayInMs: number;

    /**
     * Minimum delay offet in milliseconds.
     * Default value is 1.
     * @type {number}
     */
    private minOffsetInMs: number;

    /**
     * Initializes a new instance of the 'ExponentialBackoffRetry' class.
     *
     * @constructor
     * @param loggingService {ILoggingService}
     * @param maxRetries? {number} The maximum number of times to retry the operation. Default is 5 and maximum value allowed is 10.
     * @param baseRetryDelayInMs? {number} The base delay in milliseconds for each retry. Default is 100.
     * @param maxRetryDelayInMs? {number} The maximum number of milliseconds between two retries. Default and maximum allowed is 2000.
     * @param factor? {number} The exponential factor to use. Default is 2.
     * @param minOffsetInMs? {number} The minimum delay in milliseconds offset to use. Default is 1.
     * @param maxOffsetInMs? {number} The maximum delay in milliseconds offset to use. Default is 25.
     */
    constructor(
        loggingService: ILoggingService,
        maxRetries?: number,
        baseRetryDelayInMs?: number,
        maxRetryDelayInMs?: number,
        factor?: number,
        minOffsetInMs?: number,
        maxOffsetInMs?: number) {
        if (!loggingService) {
            throw new Error("loggingService is required for ExponentialBackoffRetry.");
        }

        const loggedExceptionManager = new LoggedExceptionManager(loggingService);

        this.logInvalidParams(
            loggedExceptionManager,
            maxRetries,
            baseRetryDelayInMs,
            maxRetryDelayInMs,
            factor,
            maxOffsetInMs
        );

        this.loggingService = loggingService;
        this.maxRetries = maxRetries || ExponentialBackoffRetry.defaultMaxRetries;
        this.baseRetryDelayInMs = baseRetryDelayInMs || ExponentialBackoffRetry.defaultBaseRetryDelayInMs;
        this.maxRetryDelayInMs = maxRetryDelayInMs || ExponentialBackoffRetry.maxAllowedRetryDelayInMs;
        this.factor = factor || ExponentialBackoffRetry.defaultFactor;
        this.minOffsetInMs = minOffsetInMs || ExponentialBackoffRetry.defaultMinOffsetInMs;
        this.maxOffsetInMs = maxOffsetInMs || ExponentialBackoffRetry.defaultMaxOffsetInMs;

        if (canUseDOM()) {
            this.loggingService.infoCallback(() => "ExponentialBackoffRetry has been configured.", null, { maxRetries, minTimeout: baseRetryDelayInMs, maxTimeout: maxRetryDelayInMs, factor });
        }
    }

    /**
     * Execute given request and retry it as configured.
     * @param serviceCall {() => Promise<Response} The request to execute and retry.
     * @param requestInfo? {any} Additional request info to include in logging.
     * @param activity? {IActivityLogItem} Optional activity to associate logged messages with.
     * @returns {Promise<Response>}
     */
    public async executeRequest(serviceCall: () => Promise<Response>, requestInfo?: any, activity?: IActivityLogItem): Promise<Response> {
        return new Promise<Response>(async(resolve, reject) => {
            await this.tryRequest(serviceCall, resolve, reject, 0 /* currentRetry is 0 based */, requestInfo, activity);
        });
    }

    /**
     * Log valid params to the class constructor.
     *
     * @constructor
     * @param loggedExceptionManager {LoggedExceptionManager}
     * @param maxRetries? {number} The maximum number of times to retry the operation. Default is 5 and maximum value allowed is 10.
     * @param baseRetryDelayInMs? {number} The base delay in milliseconds for each retry. Default is 100.
     * @param maxRetryDelayInMs? {number} The maximum number of milliseconds between two retries. Default and maximum allowed is 2000.
     * @param factor? {number} The exponential factor to use. Default is 2.
     */
    private logInvalidParams(
        loggedExceptionManager: LoggedExceptionManager,
        maxRetries?: number,
        baseRetryDelayInMs?: number,
        maxRetryDelayInMs?: number,
        factor?: number,
        maxOffsetInMs?: number): void {
        if (maxRetries && (maxRetries < 1 || maxRetries > ExponentialBackoffRetry.maxAllowedRetries)) {
            loggedExceptionManager.fatal(`maxRetries should be at least 1 and less than ${ExponentialBackoffRetry.maxAllowedRetries}.`);
        }

        if (baseRetryDelayInMs && baseRetryDelayInMs < ExponentialBackoffRetry.defaultBaseRetryDelayInMs) {
            loggedExceptionManager.fatal("baseRetryDelay has to be a positive value greater than or equal to 100.");
        }

        if (baseRetryDelayInMs && maxOffsetInMs && baseRetryDelayInMs <= maxOffsetInMs) {
            loggedExceptionManager.fatal("baseRetryDelay must not be smaller than the maximum delay offset.");
        }

        if (maxRetryDelayInMs && (maxRetryDelayInMs <= 0 || maxRetryDelayInMs > ExponentialBackoffRetry.maxAllowedRetryDelayInMs)) {
            loggedExceptionManager.fatal(`maxRetryDelay has to be a positive value less than ${ExponentialBackoffRetry.maxAllowedRetryDelayInMs}.`);
        }

        if (factor && factor <= 0) {
            loggedExceptionManager.fatal("factor has to be a positive value.");
        }
    }

    /**
     * Try the request if it fails with a transient error.
     * @param serviceCall {() => Promise<T>} The request to execute and retry.
     * @param currentRetry {number} current retry count, 0 based.
     * @param requestInfo? {TRequestInfo} Additional request info to include in logging.
     * @param activity? {IActivityLogItem} Optional activity to associate logged messages with.
     * @returns {Promise<void>}
     */
    private async tryRequest(
        serviceCall: () => Promise<Response>,
        resolve: (result?: any) => void,
        reject: (error: any) => void,
        currentRetry: number,
        requestInfo?: any,
        activity?: IActivityLogItem): Promise<void> {
        try {
            const timeoutInMs = 10000; // Default timeout is 10 sec

            // Note that abortableFetch has its own internal timeout.
            // If so if that is used inside serviceCall and timer ran out,
            // error will be thrown and it will also end this outer layer timeout early.
            const response = await this.timeoutServiceRequest(timeoutInMs, serviceCall());
            resolve(response);
        } catch (error) {
            // If the error is transient and retry if we didn't exceed the retry count.
            // On server side we do not retry failed requests.
            if (canUseDOM() && (currentRetry < this.maxRetries)) {
                // Offset for delay, in the instance where there is some sort of outage and all clients retry at the same interval. In this case it will stagger the retries.
                const fractionDigit = 3;
                const offset = parseFloat((Math.random() * (this.minOffsetInMs - this.maxOffsetInMs) + this.maxOffsetInMs).toFixed(fractionDigit));
                // Weather to add or subtract the random element.
                const half = 0.5;
                const plusOrMinus = Math.random() < half ? -1 : 1;
                // Delay the retry based on the factor and the current retry count. Do not exceed maximum delay allowed.
                const delay = Math.min(this.baseRetryDelayInMs * Math.pow(this.factor, currentRetry), this.maxRetryDelayInMs);
                // Offset the delay either positively or negatively by the offset.
                const delayWithOffset = delay + (offset * plusOrMinus);
                this.loggingService.warnCallback(() => `Retrying the request in ${delayWithOffset} milliseconds.`, activity, { error, currentRetry: currentRetry + 1, requestInfo });
                setTimeout(() => this.tryRequest(serviceCall, resolve, reject, currentRetry + 1, requestInfo, activity), delayWithOffset);
            } else {
                // Give up retrying.
                this.loggingService.errorCallback(
                    () => (canUseDOM() && (currentRetry < this.maxRetries)) ? `ExponentialBackoffRetry has exceeded maximum retries ${this.maxRetries}` : "Request has failed.",
                    activity,
                    { error, currentRetry, requestInfo });
                reject(error);
            }
        }
    }

    /**
     * Timeout functionality for service call;
     * This is not a connection timeout but rather a response timeout
     *
     * @param {number} timeoutInMs - The timeout in ms
     * @param {Promise<T>} promise - The promise for service call
     * @return {Promise<Response>} - A promise of the service response
     */
    private async timeoutServiceRequest<T>(timeoutInMs: number, promise: Promise<T>): Promise<T> {
        return new Promise<T>(async (resolve, reject) => {
            const timeoutId = setTimeout(() => reject(new Error("Service Request Timed out!")), timeoutInMs);

            try {
                const response = await promise;
                resolve(response);
                clearTimeout(timeoutId);
            } catch (error) {
                reject(error);
                clearTimeout(timeoutId);
            }
        });
    }
}
